import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiCallError, type FeedItem, type ListItemsFilters } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Items page — read-only feed of what's been ingested + summarised + rated.
 *
 * Companion to the weekly digest: lets the operator consume the content
 * inline rather than waiting for (or generating) the Sunday email. Each
 * row exposes the rating (tier + quality), a summary, and a link out to
 * the original article.
 *
 * Filters are intentionally simple — tier and "type" (RSS / Sitemap /
 * GitHub releases via tag). No full-text search, no per-source filter
 * in v1; both can be added when the volume justifies them.
 *
 * Pagination: server clamps `limit` to [1, 200]. The UI fetches 50 at a
 * time and uses a "Load more" button for offset+=50 — simpler than a
 * page-numbered control for a single-user read-only view.
 */

type TierFilter = 'all' | 'high' | 'B' | 'C';
type TypeFilter = 'all' | 'rss' | 'sitemap' | 'github' | 'youtube';

const PAGE_SIZE = 50;

function tierToFilterValue(filter: TierFilter): readonly ('S' | 'A' | 'B' | 'C')[] | undefined {
  switch (filter) {
    case 'all':
      return undefined;
    case 'high':
      return ['S', 'A'];
    case 'B':
      return ['B'];
    case 'C':
      return ['C'];
  }
}

function typeToApiFilter(filter: TypeFilter): Pick<ListItemsFilters, 'source_type' | 'tag'> {
  switch (filter) {
    case 'all':
      return {};
    case 'rss':
      return { source_type: 'rss' };
    case 'sitemap':
      return { source_type: 'sitemap' };
    case 'github':
      // GitHub releases ride the RSS poller under source_type='rss' but
      // are distinguished by the 'github_repo' tag (see ARCHITECTURE.md
      // § GitHub repo watching).
      return { tag: 'github_repo' };
    case 'youtube':
      // Phase 7a — items ingested via the local yt-ingest CLI → Transcribe
      // → summarise pipeline. source_type='youtube' is set by the
      // youtube-ingest Lambda when it INSERTs the feed_items row.
      return { source_type: 'youtube' };
  }
}

function tierBadgeClass(tier: FeedItem['tier']): string {
  switch (tier) {
    case 'S':
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
    case 'A':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
    case 'B':
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    case 'C':
      return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
    default:
      return 'bg-bg-raised text-ink-dim border-bg-line';
  }
}

function formatRelative(isoString: string): string {
  const ts = new Date(isoString).getTime();
  if (Number.isNaN(ts)) return isoString;
  const deltaMs = Date.now() - ts;
  const sec = Math.round(deltaMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 14) return `${day}d ago`;
  return isoString.slice(0, 10);
}

export function Items(): JSX.Element {
  const auth = useAuth();
  const idToken = auth.idToken;

  const [items, setItems] = useState<readonly FeedItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [tierFilter, setTierFilter] = useState<TierFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const filters = useMemo<ListItemsFilters>(() => {
    const tier = tierToFilterValue(tierFilter);
    const typeFilters = typeToApiFilter(typeFilter);
    return {
      ...(tier !== undefined ? { tier } : {}),
      ...typeFilters,
    };
  }, [tierFilter, typeFilter]);

  // `load` takes the starting offset as an explicit parameter rather than
  // closing over the `offset` state. This breaks the dependency cycle Codex
  // flagged on PR #90: if `load` depended on `offset`, every successful
  // fetch would update `offset` → recreate `load` → refire the reset effect
  // → clear items + refetch page 0 → infinite loop. With `startOffset` as a
  // param, `load` only depends on identity + filter shape, and the reset
  // effect can safely include it without looping.
  const load = useCallback(
    async (startOffset: number, signal?: AbortSignal): Promise<void> => {
      if (idToken === null) return;
      setLoading(true);
      try {
        const result = await api.listItems(
          idToken,
          { ...filters, limit: PAGE_SIZE, offset: startOffset },
          signal,
        );
        if (startOffset === 0) {
          setItems(result.items);
          setExpanded(new Set());
        } else {
          setItems((prev) => [...prev, ...result.items]);
        }
        setOffset(startOffset + result.items.length);
        setHasMore(result.items.length === PAGE_SIZE);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof ApiCallError ? err.message : 'failed to load items');
      } finally {
        setLoading(false);
      }
    },
    [idToken, filters],
  );

  // Reset + reload whenever the filter shape changes (or sign-in state
  // changes). Page-0 fetch only; subsequent pages are fetched via the
  // "Load more" button below.
  useEffect(() => {
    const ac = new AbortController();
    void load(0, ac.signal);
    return () => ac.abort();
  }, [load]);

  const toggleExpanded = useCallback((id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="font-mono text-lg uppercase tracking-wider text-ink">items</h1>
        <span className="text-2xs font-mono text-ink-muted">
          {items.length} loaded{hasMore ? ' · more available' : ''}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 text-2xs font-mono uppercase tracking-wider">
        <FilterChips
          label="tier"
          value={tierFilter}
          onChange={setTierFilter}
          options={[
            { value: 'all', label: 'all' },
            { value: 'high', label: 'S+A' },
            { value: 'B', label: 'B' },
            { value: 'C', label: 'C' },
          ]}
        />
        <FilterChips
          label="type"
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { value: 'all', label: 'all' },
            { value: 'rss', label: 'rss' },
            { value: 'sitemap', label: 'sitemap' },
            { value: 'github', label: 'github releases' },
            { value: 'youtube', label: 'youtube' },
          ]}
        />
      </div>

      {error !== null && (
        <div className="border border-rose-500/40 bg-rose-500/10 text-rose-300 px-3 py-2 mb-4 text-xs font-mono">
          {error}
        </div>
      )}

      {items.length === 0 && !loading && error === null && (
        <div className="text-center py-12 text-sm font-mono text-ink-muted">
          no items match this filter
        </div>
      )}

      <ul className="space-y-3">
        {items.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            expanded={expanded.has(item.id)}
            onToggleExpanded={() => toggleExpanded(item.id)}
          />
        ))}
      </ul>

      {hasMore && (
        <div className="text-center mt-6">
          <button
            onClick={() => void load(offset)}
            disabled={loading}
            className="px-4 py-2 text-2xs font-mono uppercase tracking-wider bg-bg-raised border border-bg-line text-ink hover:border-accent/40 hover:text-accent transition-colors disabled:opacity-50"
          >
            {loading ? 'loading…' : 'load more'}
          </button>
        </div>
      )}
    </div>
  );
}

interface FilterChipsProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly options: readonly { readonly value: T; readonly label: string }[];
}

function FilterChips<T extends string>({ label, value, onChange, options }: FilterChipsProps<T>): JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <span className="text-ink-muted mr-1">{label}:</span>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={[
            'px-2 py-1 rounded border transition-colors',
            value === opt.value
              ? 'bg-accent/10 text-accent border-accent/30'
              : 'text-ink-dim hover:text-ink hover:bg-bg-raised border-bg-line',
          ].join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

interface ItemCardProps {
  readonly item: FeedItem;
  readonly expanded: boolean;
  readonly onToggleExpanded: () => void;
}

function ItemCard({ item, expanded, onToggleExpanded }: ItemCardProps): JSX.Element {
  const isGithubRelease = item.source_tags.includes('github_repo');
  const sourceTypeLabel = isGithubRelease ? 'github releases' : item.source_type;
  const summary = expanded && item.summary_medium !== null ? item.summary_medium : item.summary_short;
  const canExpand = item.summary_medium !== null && item.summary_medium !== item.summary_short;

  return (
    <li className="border border-bg-line bg-bg-surface/40 rounded p-4 hover:border-bg-line/80 transition-colors">
      <div className="flex items-start gap-3 mb-2">
        <span
          className={[
            'px-2 py-0.5 rounded border text-2xs font-mono uppercase tracking-wider',
            tierBadgeClass(item.tier),
          ].join(' ')}
          title={`tier ${item.tier ?? '—'} · quality ${item.quality_score ?? '—'}/100`}
        >
          {item.tier ?? '—'} · {item.quality_score ?? '—'}
        </span>

        <div className="flex-1 min-w-0">
          {item.url !== null ? (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-ink hover:text-accent transition-colors break-words"
            >
              {item.title}
            </a>
          ) : (
            <span className="text-sm text-ink break-words">{item.title}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-2xs font-mono text-ink-muted mb-2">
        <span>{item.source_name}</span>
        <span className="px-1.5 py-0.5 rounded border border-bg-line bg-bg-raised text-ink-dim">
          {sourceTypeLabel}
        </span>
        <span title={item.ingested_at}>{formatRelative(item.ingested_at)}</span>
        {item.routed_destinations.length > 0 && (
          <span className="text-ink-dim">→ {item.routed_destinations.join(', ')}</span>
        )}
      </div>

      {summary !== null && summary !== '' && (
        <div className="text-xs text-ink-dim leading-relaxed whitespace-pre-wrap break-words">
          {summary}
        </div>
      )}

      {canExpand && (
        <button
          onClick={onToggleExpanded}
          className="mt-2 text-2xs font-mono uppercase tracking-wider text-ink-muted hover:text-accent transition-colors"
        >
          {expanded ? '— collapse' : '+ expand'}
        </button>
      )}
    </li>
  );
}
